import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import Button from './Button';
import UsuariosServiceAPI, { Usuario } from '../../services/usuariosServiceAPI';
import { PerfisService, Perfil } from '../../services/perfisService';
import FiliaisServiceAPI, { Filial } from '../../services/filiaisServiceAPI';

interface EditarUsuarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  usuario: Usuario | null;
  onSave: () => void;
}

const EditarUsuarioModal: React.FC<EditarUsuarioModalProps> = ({
  isOpen,
  onClose,
  usuario,
  onSave
}) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    login: '',
    perfil_id: '',
    filiais_permitidas: [] as string[],
    ativo: true
  });
  const [loading, setLoading] = useState(false);
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [loadingPerfis, setLoadingPerfis] = useState(false);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [loadingFiliais, setLoadingFiliais] = useState(false);

  useEffect(() => {
    if (usuario) {
      setFormData({
        nome: usuario.nome || '',
        email: usuario.email || '',
        login: usuario.login || '',
        perfil_id: usuario.perfil_id || '',
        filiais_permitidas: usuario.filiais_permitidas || [],
        ativo: usuario.ativo !== false
      });
    }
  }, [usuario]);

  useEffect(() => {
    const carregarPerfis = async () => {
      try {
        setLoadingPerfis(true);
        const { data } = await PerfisService.buscarTodos();
        if (data) {
          setPerfis(data);
        }
      } catch (error) {
        console.error('Erro ao carregar perfis:', error);
      } finally {
        setLoadingPerfis(false);
      }
    };

    const carregarFiliais = async () => {
      try {
        setLoadingFiliais(true);
        const data = await FiliaisServiceAPI.buscarTodas();
        setFiliais(data);
      } catch (error) {
        console.error('Erro ao carregar filiais:', error);
      } finally {
        setLoadingFiliais(false);
      }
    };

    if (isOpen) {
      carregarPerfis();
      carregarFiliais();
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!usuario) return;

    try {
      setLoading(true);
      
      // Remover campos vazios antes de enviar
      const dataToSend: any = { ...formData };
      if (!dataToSend.email) delete dataToSend.email;
      
      await UsuariosServiceAPI.atualizar(usuario.id, dataToSend);

      alert('Usuário atualizado com sucesso!');
      onSave();
      onClose();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      alert('Erro ao atualizar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Editar Usuário">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <Input
            label="Nome"
            value={formData.nome}
            onChange={(e: any) => handleChange('nome', e.target.value)}
            required
          />
          
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e: any) => handleChange('email', e.target.value)}
            required
          />
          
          <Input
            label="Login"
            value={formData.login}
            onChange={(e: any) => handleChange('login', e.target.value)}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Perfil
            </label>
            <select
              value={formData.perfil_id}
              onChange={(e) => handleChange('perfil_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={loadingPerfis}
            >
              <option value="">Selecione um perfil</option>
              {perfis.map(perfil => (
                <option key={perfil.id} value={perfil.id}>
                  {perfil.nome}
                </option>
              ))}
            </select>
            {loadingPerfis && (
              <p className="text-sm text-gray-500 mt-1">Carregando perfis...</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filiais Permitidas
            </label>
            <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
              {loadingFiliais ? (
                <p className="text-sm text-gray-500">Carregando filiais...</p>
              ) : (
                filiais.map(filial => (
                  <div key={filial.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`filial-${filial.id}`}
                      checked={formData.filiais_permitidas.includes(filial.id || '')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleChange('filiais_permitidas', [...formData.filiais_permitidas, filial.id]);
                        } else {
                          handleChange('filiais_permitidas', formData.filiais_permitidas.filter(id => id !== filial.id));
                        }
                      }}
                      className="mr-2"
                    />
                    <label htmlFor={`filial-${filial.id}`} className="text-sm">
                      {filial.nome}
                    </label>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="ativo"
              checked={formData.ativo}
              onChange={(e) => handleChange('ativo', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="ativo">Usuário Ativo</label>
          </div>
        </div>
        
        <div className="flex justify-end space-x-2 mt-6">
          <Button
            type="button"
            onClick={onClose}
            className="bg-gray-500 hover:bg-gray-600"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditarUsuarioModal;
